import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, MapPin, Users, Flame, Sparkles, Zap,
  Calendar as CalendarIcon, Filter, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GroupTripCalendar = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date(2026, 3, 1)); // Start at April 2026
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedBurnLevel, setSelectedBurnLevel] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const burnIcons = { 1: Sparkles, 2: Flame, 3: Zap };
  const burnColors = {
    1: 'bg-purple-600',
    2: 'bg-orange-500',
    3: 'bg-red-600'
  };
  const burnGradients = {
    1: 'from-purple-500 to-pink-500',
    2: 'from-orange-500 to-amber-500',
    3: 'from-red-500 to-orange-600'
  };

  const regions = [
    { key: 'north_central_america', name: 'North & Central America', name_es: 'Norte y Centroamérica' },
    { key: 'south_america', name: 'South America', name_es: 'Sudamérica' },
    { key: 'caribbean', name: 'Caribbean', name_es: 'Caribe' },
  ];

  const burnLevels = [
    { key: '1', name: 'The Socialite', name_es: 'El Socialité' },
    { key: '2', name: 'The Vibe', name_es: 'La Vibra' },
    { key: '3', name: 'The Badass', name_es: 'El Temerario' },
  ];

  useEffect(() => {
    const fetchTrips = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('lang', language);
        if (selectedRegion) params.append('region', selectedRegion);
        if (selectedBurnLevel) params.append('burn_level', selectedBurnLevel);

        const response = await fetch(`${API_URL}/api/trips?${params.toString()}`);
        const data = await response.json();
        setTrips(data.trips || []);
      } catch (error) {
        console.error('Error fetching trips:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrips();
  }, [language, selectedRegion, selectedBurnLevel]);

  // Get all dates with trips for the current month
  const getTripsForMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const tripsByDate = {};

    trips.forEach(trip => {
      if (trip.dates && trip.dates.length > 0) {
        trip.dates.forEach(dateStr => {
          const date = new Date(dateStr);
          if (date.getFullYear() === year && date.getMonth() === month) {
            const dayKey = date.getDate();
            if (!tripsByDate[dayKey]) {
              tripsByDate[dayKey] = [];
            }
            tripsByDate[dayKey].push({
              ...trip,
              departureDate: dateStr
            });
          }
        });
      }
    });

    return tripsByDate;
  };

  const tripsByDate = getTripsForMonth();

  // Calendar generation
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    setSelectedDate(null);
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    setSelectedDate(null);
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);
  const dayNames = language === 'es' 
    ? ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const clearFilters = () => {
    setSelectedRegion('');
    setSelectedBurnLevel('');
  };

  const hasActiveFilters = selectedRegion || selectedBurnLevel;

  // Get trips for selected date
  const selectedDateTrips = selectedDate ? tripsByDate[selectedDate] || [] : [];

  return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid="group-trip-calendar">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-block px-4 py-2 bg-[#84bc00]/20 text-[#84bc00] text-sm font-bold rounded-full mb-4">
            {language === 'es' ? 'VIAJES GRUPALES' : 'GROUP TRIPS'}
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4">
            {language === 'es' ? 'Calendario de Salidas' : 'Departure Calendar'}
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            {language === 'es' 
              ? 'Encuentra una fecha que funcione para ti y únete a un grupo de viajeros' 
              : 'Find a date that works for you and join a group of travelers'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-8" data-testid="calendar-filters">
          <div className="flex flex-wrap items-center gap-4">
            {/* Region Filter */}
            <div className="relative">
              <select
                data-testid="calendar-region-filter"
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="appearance-none bg-gray-100 border-0 rounded-lg px-4 py-2.5 pr-10 font-medium text-gray-700 focus:ring-2 focus:ring-[#84bc00] cursor-pointer"
              >
                <option value="">{language === 'es' ? 'Todas las Regiones' : 'All Regions'}</option>
                {regions.map((r) => (
                  <option key={r.key} value={r.key}>
                    {language === 'es' ? r.name_es : r.name}
                  </option>
                ))}
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none rotate-90" />
            </div>

            {/* Burn Level Filter */}
            <div className="relative">
              <select
                data-testid="calendar-burn-filter"
                value={selectedBurnLevel}
                onChange={(e) => setSelectedBurnLevel(e.target.value)}
                className="appearance-none bg-gray-100 border-0 rounded-lg px-4 py-2.5 pr-10 font-medium text-gray-700 focus:ring-2 focus:ring-[#84bc00] cursor-pointer"
              >
                <option value="">{language === 'es' ? 'Todos los Niveles' : 'All Levels'}</option>
                {burnLevels.map((b) => (
                  <option key={b.key} value={b.key}>
                    Level {b.key}: {language === 'es' ? b.name_es : b.name}
                  </option>
                ))}
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none rotate-90" />
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                variant="ghost"
                className="text-gray-500 hover:text-gray-700"
                data-testid="calendar-clear-filters"
              >
                <X className="w-4 h-4 mr-1" />
                {language === 'es' ? 'Limpiar' : 'Clear'}
              </Button>
            )}

            {/* Trip Count */}
            <div className="ml-auto text-gray-500 text-sm">
              {trips.length} {trips.length === 1 
                ? (language === 'es' ? 'viaje disponible' : 'trip available') 
                : (language === 'es' ? 'viajes disponibles' : 'trips available')}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Calendar Header */}
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-4 flex items-center justify-between">
                <button
                  onClick={prevMonth}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                  data-testid="prev-month-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="text-xl font-bold text-white capitalize" data-testid="current-month">
                  {formatMonthYear(currentMonth)}
                </h2>
                <button
                  onClick={nextMonth}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                  data-testid="next-month-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Day Names */}
              <div className="grid grid-cols-7 bg-gray-100">
                {dayNames.map((day) => (
                  <div key={day} className="p-3 text-center text-sm font-bold text-gray-500">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7">
                {/* Empty cells for days before first day of month */}
                {Array.from({ length: firstDay }).map((_, i) => (
                  <div key={`empty-${i}`} className="p-2 min-h-[100px] bg-gray-50 border-b border-r border-gray-100"></div>
                ))}

                {/* Days of the month */}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dayTrips = tripsByDate[day] || [];
                  const hasTrips = dayTrips.length > 0;
                  const isSelected = selectedDate === day;
                  const isToday = new Date().getDate() === day && 
                                  new Date().getMonth() === currentMonth.getMonth() &&
                                  new Date().getFullYear() === currentMonth.getFullYear();

                  return (
                    <div
                      key={day}
                      data-testid={`calendar-day-${day}`}
                      onClick={() => hasTrips && setSelectedDate(day)}
                      className={`
                        p-2 min-h-[100px] border-b border-r border-gray-100 transition-colors
                        ${hasTrips ? 'cursor-pointer hover:bg-[#84bc00]/5' : 'bg-gray-50/50'}
                        ${isSelected ? 'bg-[#84bc00]/10 ring-2 ring-[#84bc00] ring-inset' : ''}
                      `}
                    >
                      {/* Day Number */}
                      <div className={`
                        text-sm font-bold mb-2
                        ${isToday ? 'w-7 h-7 rounded-full bg-[#84bc00] text-white flex items-center justify-center' : ''}
                        ${hasTrips && !isToday ? 'text-gray-900' : 'text-gray-400'}
                      `}>
                        {day}
                      </div>

                      {/* Trip Indicators */}
                      {hasTrips && (
                        <div className="space-y-1">
                          {dayTrips.slice(0, 2).map((trip, idx) => {
                            const BurnIcon = burnIcons[trip.burn_level] || Flame;
                            return (
                              <div
                                key={`${trip.id}-${idx}`}
                                className={`
                                  text-xs p-1.5 rounded ${burnColors[trip.burn_level]} text-white
                                  truncate flex items-center gap-1
                                `}
                              >
                                <BurnIcon className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate">{trip.name}</span>
                              </div>
                            );
                          })}
                          {dayTrips.length > 2 && (
                            <div className="text-xs text-gray-500 font-medium">
                              +{dayTrips.length - 2} {language === 'es' ? 'más' : 'more'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="p-4 bg-gray-50 border-t border-gray-100">
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="text-gray-500 font-medium">{language === 'es' ? 'Leyenda:' : 'Legend:'}</span>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-purple-600"></div>
                    <span className="text-gray-600">Socialite</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-orange-500"></div>
                    <span className="text-gray-600">Vibe</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-red-600"></div>
                    <span className="text-gray-600">Badass</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Selected Date Details */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              {selectedDate ? (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-[#84bc00]/10 flex items-center justify-center">
                      <CalendarIcon className="w-6 h-6 text-[#84bc00]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-gray-900">
                        {formatDate(`${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(selectedDate).padStart(2, '0')}`)}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {selectedDateTrips.length} {selectedDateTrips.length === 1 
                          ? (language === 'es' ? 'viaje' : 'trip') 
                          : (language === 'es' ? 'viajes' : 'trips')}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4" data-testid="selected-date-trips">
                    {selectedDateTrips.map((trip) => {
                      const BurnIcon = burnIcons[trip.burn_level] || Flame;
                      const burnGradient = burnGradients[trip.burn_level] || burnGradients[2];

                      return (
                        <div
                          key={`${trip.id}-${trip.departureDate}`}
                          data-testid={`trip-item-${trip.id}`}
                          className="p-4 border border-gray-100 rounded-xl hover:border-[#84bc00] transition-colors group"
                        >
                          {/* Trip Image */}
                          <div className="relative h-32 rounded-lg overflow-hidden mb-3">
                            <img
                              src={trip.image}
                              alt={trip.name}
                              className="w-full h-full object-cover"
                            />
                            <div className={`absolute top-2 left-2 px-2 py-1 rounded-full ${burnColors[trip.burn_level]} text-white text-xs font-bold flex items-center gap-1`}>
                              <BurnIcon className="w-3 h-3" />
                              {trip.burn_label}
                            </div>
                          </div>

                          {/* Trip Info */}
                          <h4 className="font-bold text-gray-900 mb-1 group-hover:text-[#84bc00] transition-colors">
                            {trip.name}
                          </h4>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                            <MapPin className="w-3.5 h-3.5" />
                            <span>{trip.location}</span>
                          </div>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              <Users className="w-3.5 h-3.5" />
                              <span>{trip.spots} {language === 'es' ? 'lugares' : 'spots'}</span>
                            </div>
                            <span className="font-bold text-[#84bc00]">
                              ${trip.price?.toLocaleString()}
                            </span>
                          </div>

                          <Button
                            onClick={() => navigate(`/trips/${trip.id}`)}
                            className={`w-full bg-gradient-to-r ${burnGradient} hover:opacity-90 text-white font-bold text-sm py-2`}
                          >
                            {language === 'es' ? 'Ver y Reservar' : 'View & Book'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {language === 'es' ? 'Selecciona una fecha' : 'Select a date'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {language === 'es' 
                      ? 'Haz clic en un día con viajes para ver los detalles' 
                      : 'Click on a day with trips to see details'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupTripCalendar;
